import { t } from "@lingui/macro";
import {
	appEnvironment,
	appEnvironmentLabel,
	firebaseEnvironment,
	firebaseEnvironmentLabel,
} from "@repo/common";
import {
	Briefcase,
	Building,
	LayoutDashboard,
	ListChecks,
	LogOut,
	Monitor,
	Moon,
	MoreHorizontal,
	Phone,
	Settings,
	Sun,
	Users,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useState } from "react";
import { useData } from "../contexts/DataContext";
import { useOutboundPlaybookFeatureFlag } from "../hooks/useOutboundPlaybookFeatureFlag";
import type { AppUser } from "../types";
import { isAdminRole, isSystemAdminRole } from "../utils/auth";
import { SwitchCompanyModal } from "./SwitchCompanyModal";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

declare const __COMMIT_HASH__: string;
declare const __BUILD_DATE__: string;

interface NavigationProps {
	currentPage: string;
	onPageChange: (page: string) => void;
	user: AppUser | null;
	onLogout: () => void;
}

export function Navigation({
	currentPage,
	onPageChange,
	user,
	onLogout,
}: NavigationProps) {
	const { language, companyProfile } = useData();
	const { theme = "system", setTheme } = useTheme();
	const outboundPlaybooksEnabled = useOutboundPlaybookFeatureFlag(
		user?.companyId,
		companyProfile,
	);
	const [isSwitchCompanyModalOpen, setIsSwitchCompanyModalOpen] =
		useState(false);
	const showEnvironmentBadges =
		appEnvironment !== "production" || firebaseEnvironment !== "prod";
	const appEnvironmentBadgeClassName = {
		development: "border-ring/30 bg-accent text-accent-foreground",
		staging: "border-warn/30 bg-warn-tint text-foreground",
		production: "border-live/30 bg-live-tint text-foreground",
	}[appEnvironment];
	const firebaseEnvironmentBadgeClassName =
		firebaseEnvironment === "prod"
			? "border-live/30 bg-live-tint text-foreground"
			: "border-warn/30 bg-warn-tint text-foreground";

	const regularNavItems = [
		{
			id: "dashboard",
			label: t({ id: "dashboard", message: "Dashboard" }),
			icon: LayoutDashboard,
		},
		{ id: "calls", label: t({ id: "calls", message: "Calls" }), icon: Phone },
		{
			id: "playbook-runs",
			label: t({
				id: "navigation.playbookRuns",
				message: "Playbook Runs",
			}),
			icon: ListChecks,
		},
		{
			id: "agents",
			label: t({ id: "agents", message: "Agents" }),
			icon: Users,
		},
		{
			id: "services",
			label: t({ id: "services", message: "Services" }),
			icon: Briefcase,
		},
		{
			id: "settings",
			label: t({ id: "settings", message: "Settings" }),
			icon: Settings,
		},
	];

	const adminNavItems: typeof regularNavItems = [
		// { id: "admin", label: t("admin", language), icon: Shield },
	];

	// Only expose surfaces backed by live customer data.
	const visibleRegularNavItems = regularNavItems.filter((item) => {
		if (item.id === "playbook-runs") {
			return outboundPlaybooksEnabled;
		}
		return true;
	});

	// Combine regular and admin nav items (admin items only visible to admins)
	const visibleNavItems = isAdminRole(user?.role)
		? [...visibleRegularNavItems, ...adminNavItems]
		: visibleRegularNavItems;

	// Helper for progressive icon bar and More dropdown breakpoints
	// Sequence:
	// 1. Desktop (>= 1024px): Full labels.
	// 2. 520px - 1023px: All icons visible + "Provoize" text (no More menu!).
	// 3. Below 520px: "Provoize" text hides first. All icons still visible (no More menu!).
	// 4. When room runs out: Last 2 items ALWAYS collapse into 'More' together so 'More' never holds only 1 item!
	const getNavItemVisibility = (index: number, totalItems: number) => {
		if (index < 3) {
			return {
				bar: "flex lg:hidden",
				dropdown: "hidden",
			};
		}

		// Ensure the last item (e.g. Settings) shares the breakpoint of second-to-last item
		// so both move into/out of 'More' together, preventing a 1-item dropdown menu.
		const effectiveIndex =
			index === totalItems - 1 && totalItems >= 4 ? totalItems - 2 : index;

		switch (effectiveIndex) {
			case 3:
				return {
					bar: "hidden min-[330px]:flex lg:hidden",
					dropdown: "flex min-[330px]:hidden",
				};
			case 4:
				return {
					bar: "hidden min-[370px]:flex lg:hidden",
					dropdown: "flex min-[370px]:hidden",
				};
			case 5:
			case 6:
			default:
				return {
					bar: "hidden min-[420px]:flex lg:hidden",
					dropdown: "flex min-[420px]:hidden",
				};
		}
	};

	const getDropdownTriggerVisibilityClass = (totalItems: number) => {
		if (totalItems <= 3) return "hidden";
		const secondToLastIndex = Math.max(3, totalItems - 2);
		switch (secondToLastIndex) {
			case 3:
				return "flex min-[330px]:hidden";
			case 4:
				return "flex min-[370px]:hidden";
			case 5:
			case 6:
			default:
				return "flex min-[420px]:hidden";
		}
	};

	const getMoreButtonActiveClass = (
		navItems: typeof visibleNavItems,
		activePage: string,
	) => {
		const activeIndex = navItems.findIndex((item) => item.id === activePage);
		if (activeIndex < 3) return "";

		const totalItems = navItems.length;
		const effectiveIndex =
			activeIndex === totalItems - 1 && totalItems >= 4
				? totalItems - 2
				: activeIndex;

		switch (effectiveIndex) {
			case 3:
				return "max-[329px]:bg-accent max-[329px]:text-accent-foreground";
			case 4:
				return "max-[369px]:bg-accent max-[369px]:text-accent-foreground";
			case 5:
			case 6:
			default:
				return "max-[419px]:bg-accent max-[419px]:text-accent-foreground";
		}
	};

	const dropdownTriggerClass = getDropdownTriggerVisibilityClass(
		visibleNavItems.length,
	);
	const moreButtonActiveClass = getMoreButtonActiveClass(
		visibleNavItems,
		currentPage,
	);

	const hasCommitHash = !!__COMMIT_HASH__ && __COMMIT_HASH__ !== "dev";
	const buildLabel = hasCommitHash
		? new Date(__BUILD_DATE__).toLocaleString(undefined, {
				month: "short",
				day: "numeric",
				hour: "2-digit",
				minute: "2-digit",
			})
		: language === "he"
			? "גרסת פיתוח"
			: "Dev Build";
	const revisionLabel = hasCommitHash
		? `${language === "he" ? "גרסה" : "Revision"}: ${__COMMIT_HASH__}`
		: language === "he"
			? "גרסה: dev"
			: "Revision: dev";

	return (
		<nav className="border-b border-border bg-card px-2 py-1.5 sm:px-4 sm:py-2">
			<div className="mx-auto max-w-7xl flex flex-col gap-1.5">
				{/* Main Row */}
				<div className="flex items-center gap-2 md:gap-3">
					{/* 1. Icon */}
					<Tooltip delayDuration={3000}>
						<TooltipTrigger asChild>
							<img
								src="/provoize_icon.svg"
								alt="Logo"
								className="h-8 w-auto shrink-0"
							/>
						</TooltipTrigger>
						<TooltipContent side="bottom" sideOffset={6}>
							<div className="space-y-1">
								<div>{buildLabel}</div>
								<div>{revisionLabel}</div>
							</div>
						</TooltipContent>
					</Tooltip>

					{/* 2. Provoize (Hides on mobile < 520px before further icon collapsing) */}
					<span className="hidden min-[520px]:inline min-w-0 truncate text-base leading-tight sm:text-xl">
						{t({ id: "navigation.brandName", message: "Provoize" })}
					</span>

					{/* 3. Desktop Menu Items (Icon + Label) */}
					<div className="hidden lg:flex items-center gap-1 space-x-1 rtl:space-x-reverse">
						{visibleNavItems.map((item) => {
							const Icon = item.icon;
							return (
								<Button
									key={item.id}
									variant="ghost"
									onClick={() => onPageChange(item.id)}
									aria-label={item.label}
									data-testid={`nav-${item.id}`}
									className={`flex h-[38px] items-center justify-center gap-2 px-3 ${
										currentPage === item.id
											? "bg-accent text-accent-foreground hover:bg-accent"
											: ""
									}`}
								>
									<Icon className="w-4 h-4" />
									<span>{item.label}</span>
								</Button>
							);
						})}
					</div>

					{/* 3. Mobile / Tablet Menu Items (Dynamic Breakpoints for 3, 4, 5, 6, 7 icons) */}
					<div className="flex lg:hidden items-center gap-0.5 sm:gap-1">
						{/* Icon buttons with progressive breakpoint visibility */}
						{visibleNavItems.map((item, index) => {
							const Icon = item.icon;
							const { bar } = getNavItemVisibility(
								index,
								visibleNavItems.length,
							);
							return (
								<Button
									key={item.id}
									variant="ghost"
									onClick={() => onPageChange(item.id)}
									aria-label={item.label}
									data-testid={`nav-${item.id}`}
									className={`${bar} h-[38px] w-[38px] p-0 items-center justify-center ${
										currentPage === item.id
											? "bg-accent text-accent-foreground hover:bg-accent"
											: ""
									}`}
								>
									<Icon className="w-4 h-4" />
								</Button>
							);
						})}

						{/* 'More' dropdown menu (shows overflow items at corresponding breakpoints) */}
						{visibleNavItems.length > 3 && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild className={dropdownTriggerClass}>
									<Button
										variant="ghost"
										aria-label={t({ id: "moreNav", message: "More" })}
										data-testid="nav-more"
										className={`h-9 w-9 p-0 items-center justify-center ${dropdownTriggerClass} ${moreButtonActiveClass}`}
									>
										<MoreHorizontal className="w-4 h-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent
									align={language === "he" ? "start" : "end"}
									className="w-48"
								>
									{visibleNavItems.map((item, index) => {
										if (index < 3) return null;
										const Icon = item.icon;
										const { dropdown } = getNavItemVisibility(
											index,
											visibleNavItems.length,
										);
										return (
											<DropdownMenuItem
												key={item.id}
												onClick={() => onPageChange(item.id)}
												className={`${dropdown} ${
													currentPage === item.id
														? "bg-accent font-semibold text-accent-foreground"
														: ""
												} items-center`}
											>
												<Icon
													className={`w-4 h-4 ${language === "he" ? "ml-2" : "mr-2"}`}
												/>
												<span>{item.label}</span>
											</DropdownMenuItem>
										);
									})}
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</div>

					{/* 4. Spacer */}
					<div className="flex-1" />

					{/* 6. Avatar */}
					<div className="flex items-center gap-1">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<button
									aria-label={t({
										id: "navigation.userMenu",
										message: "User menu",
									})}
									className="flex cursor-pointer items-center gap-2 rounded-full focus:outline-none focus:ring-[3px] focus:ring-ring/30"
								>
									<Avatar className="h-9 w-9 hover:opacity-80 transition-opacity">
										<AvatarImage src={user?.photoURL || undefined} />
										<AvatarFallback>
											{user?.displayName?.charAt(0).toUpperCase() ||
												user?.email?.charAt(0).toUpperCase() ||
												"U"}
										</AvatarFallback>
									</Avatar>
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								align={language === "he" ? "start" : "end"}
								className="w-56"
							>
								<DropdownMenuLabel>
									<div>
										<div className="text-sm">{user?.displayName}</div>
										<div className="text-xs text-muted-foreground">
											{user?.email}
										</div>
									</div>
								</DropdownMenuLabel>
								<DropdownMenuSeparator />
								<DropdownMenuItem onClick={() => onPageChange("settings")}>
									<Settings
										className={`w-4 h-4 ${language === "he" ? "ml-2" : "mr-2"}`}
									/>
									<span>{t({ id: "settings", message: "Settings" })}</span>
								</DropdownMenuItem>
								{isSystemAdminRole(user?.role) && (
									<DropdownMenuItem
										onClick={() => setIsSwitchCompanyModalOpen(true)}
									>
										<Building
											className={`w-4 h-4 ${language === "he" ? "ml-2" : "mr-2"}`}
										/>
										<span>
											{language === "he" ? "החלפת חברה" : "Change Company"}
										</span>
									</DropdownMenuItem>
								)}
								<DropdownMenuSeparator />
								<DropdownMenuLabel className="text-xs text-muted-foreground">
									{t({ id: "navigation.theme", message: "Theme" })}
								</DropdownMenuLabel>
								<DropdownMenuRadioGroup value={theme} onValueChange={setTheme}>
									<DropdownMenuRadioItem value="light">
										<Sun className="size-4" />
										<span>{t({ id: "theme.light", message: "Light" })}</span>
									</DropdownMenuRadioItem>
									<DropdownMenuRadioItem value="dark">
										<Moon className="size-4" />
										<span>{t({ id: "theme.dark", message: "Dark" })}</span>
									</DropdownMenuRadioItem>
									<DropdownMenuRadioItem value="system">
										<Monitor className="size-4" />
										<span>{t({ id: "theme.system", message: "System" })}</span>
									</DropdownMenuRadioItem>
								</DropdownMenuRadioGroup>
								<DropdownMenuSeparator />
								<DropdownMenuItem onClick={onLogout}>
									<LogOut
										className={`w-4 h-4 ${language === "he" ? "ml-2" : "mr-2"}`}
									/>
									<span>{t({ id: "logout", message: "Logout" })}</span>
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>

				{/* Bottom Row: Company name & App/Firebase badges (centered) */}
				{(companyProfile?.name || showEnvironmentBadges) && (
					<div className="flex flex-wrap justify-center items-center gap-2 text-xs">
						{companyProfile?.name && (
							<span className="font-semibold text-muted-foreground">
								{companyProfile.name}
							</span>
						)}
						{showEnvironmentBadges && (
							<div className="flex items-center gap-1">
								<span
									className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${appEnvironmentBadgeClassName}`}
								>
									App: {appEnvironmentLabel}
								</span>
								<span
									className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${firebaseEnvironmentBadgeClassName}`}
								>
									Firebase: {firebaseEnvironmentLabel}
								</span>
							</div>
						)}
					</div>
				)}
			</div>

			{user && (
				<SwitchCompanyModal
					user={user}
					isOpen={isSwitchCompanyModalOpen}
					onClose={() => setIsSwitchCompanyModalOpen(false)}
				/>
			)}
		</nav>
	);
}
